import { Link } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { Card, Icon } from '@/components/ui';
import { TenantSettings } from '@/pages/settings/TenantSettings';
import { useHasRole } from '@/lib/auth';
import { StorageSettingsSection } from '@/features/system';
import { useIsSystemAdmin } from '@/components/SystemAdminGuard';
import { useProvidersHealthSummary } from '@/features/llm-config/hooks/usePlatformProviders';
import { PROVIDER_METADATA } from '@/features/llm-config/types';
import type { ProviderType } from '@/features/llm-config/types';
import { getProviderStatusStyle } from '@/features/llm-config/lib/statusStyles';
import { ProviderIcon } from '@/features/llm-config/components/ProviderIcons';

// =============================================================================
// Settings Page
// =============================================================================

export function SettingsPage() {
  const { t } = useI18n();
  const isAdmin = useHasRole('admin');
  const isSystemAdmin = useIsSystemAdmin();
  const { data: healthSummary } = useProvidersHealthSummary({ enabled: isSystemAdmin });



  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('admin.settings.title')}</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">{t('admin.settings.subtitle')}</p>
      </div>

      <div className="max-w-2xl space-y-6">
        {/* Tenant/Workspace Settings */}
        <TenantSettings />

        {/* User Preferences */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-900 dark:text-gray-100">
              User Preferences
            </h3>
            <Link
              to="/settings/preferences"
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
            >
              Configure
              <Icon name="chevron-down" size={14} className="-rotate-90" />
            </Link>
          </div>
          
          {/* Timezone & Display */}
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                <Icon name="clock" size={20} className="text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <div className="font-medium text-gray-900 dark:text-gray-100">Date & Time</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Timezone, date format, and display preferences
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* LLM Providers — only visible to system tenant admins */}
        {isSystemAdmin && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-900 dark:text-gray-100">
              {t('admin.settings.llmProviders.title')}
            </h3>
            <Link
              to="/settings/platform-providers"
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
            >
              {t('common.configure')}
              <Icon name="chevron-down" size={14} className="-rotate-90" />
            </Link>
          </div>
          
          {/* Dynamic provider cards from health-summary API */}
          <div className="space-y-3">
            {healthSummary?.providers?.length ? (
              healthSummary.providers.map((p) => {
                const meta = PROVIDER_METADATA[p.provider_type as ProviderType];
                const sc = getProviderStatusStyle(p.status);
                return (
                  <div
                    key={p.id}
                    className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          meta?.iconBg || 'bg-gray-100 dark:bg-gray-700'
                        }`}
                      >
                        <ProviderIcon
                          providerType={p.provider_type}
                          size={22}
                          className={p.provider_type === 'azure_foundry'
                            ? 'text-purple-600 dark:text-purple-400'
                            : p.provider_type === 'voyage'
                              ? 'text-cyan-600 dark:text-cyan-400'
                              : p.provider_type === 'aws_bedrock'
                                ? 'text-orange-600 dark:text-orange-400'
                                : 'text-gray-600 dark:text-gray-400'}
                        />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {p.name || meta?.displayName || p.provider_type}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {meta?.description || p.provider_type}
                        </div>
                      </div>
                    </div>
                    <span className={`text-sm ${sc.text} flex items-center gap-1`}>
                      <span className={`w-2 h-2 ${sc.dot} rounded-full`}></span>
                      {t(sc.labelKey)}
                    </span>
                  </div>
                );
              })
            ) : (
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                    <Icon name="zap" size={20} className="text-gray-400" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">{t('admin.settings.platformProviders.empty')}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {t('admin.settings.llmProviders.goToPlatform')}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>
        )}



        {/* BYOM - Your LLM Providers */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-900 dark:text-gray-100">
              Your LLM Providers
            </h3>
            <Link
              to="/settings/providers"
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
            >
              Configure
              <Icon name="chevron-down" size={14} className="-rotate-90" />
            </Link>
          </div>
          
          {/* BYOM Info */}
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center">
                <Icon name="shield" size={20} className="text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <div className="font-medium text-gray-900 dark:text-gray-100">Bring Your Own Model</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Configure your own LLM provider credentials
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Platform Storage (Admin Only) */}
        {isAdmin && <StorageSettingsSection />}
      </div>
    </div>
  );
}
